/* ============================================================================
   WEAR IT WITH — the Isabel PDP's cross-sell, below the ATB where the tops
   page has its embroidery card (Figma Complete the Set 877:194945; the
   tops drawer 1448:28827).

   One of the four core scrub tops, always in the pant's colour: changing the
   pant swatch recolours every top. The underlined parts are the controls:
     • the NAME opens "Choose Your Scrub Top", a drawer of the four, each
       shown in the pant's colour; picking one marks it, and the drawer
       closes itself on the size drawer's beat;
     • the SIZE opens a size drawer for that top (the PDP's own size drawer,
       cloned, without the model line — that model wears the pant), which
       fills the card; until then it follows the size picked for the pant;
     • ADD TO BAG adds it — straight away, or right after the size drawer
       when no size is picked yet. Success: the link confirms ADDED with a
       drawn check while the page puts it in the bag (onAdd gets the image:
       the shared bag flies it to the header, the badge pops, the bag opens).
   On touch the card also swipes between the four; the progress bar below it
   (the desktop gallery's, 1485:93502) follows the drag a quarter per top. The
   name stays the way to see them all at once.

   `items` makes it any cross-sell: the set page passes the one Sydney Scrub
   Jacket (the live kits' Wear It With pick). With a single item there's no
   swipe, progress bar or picker — the NAME opens that product — and each
   item says which colour it shows for the page's (`shown`), falling back
   where it isn't made in it.
   ========================================================================== */

import { animate, stagger, MO, state, play, track, SPRING_RELEASE, $, $$ } from './mo.js';
import { CORE_TOPS, SIZE_NAMES } from './data.js';
import { createDrawer, initSizeDrawer } from './drawers.js';
import { swapText, announce, tm } from './ui.js';

const AUTO_CLOSE_MS = 450; // the size drawer's beat
const CHECK = '<svg class="xsell__check" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M3 8.5l3.2 3.2L13 4.8" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" pathLength="1"/></svg>';

/* the tops drawer — built here so both PDPs don't carry its markup */
function topsDrawerHTML() {
  return `
  <div class="drawer drawer--tops" data-drawer="tops" hidden>
    <div class="drawer__scrim" data-drawer-scrim></div>
    <div class="drawer__panel" role="dialog" aria-modal="true" aria-labelledby="tops-title" tabindex="-1" data-drawer-panel>
      <div class="tops">
        <div class="tops__head">
          <h2 class="tops__title" id="tops-title">Choose Your Scrub Top</h2>
          <button class="tops__close" type="button" aria-label="Close" data-drawer-close>
            <img src="assets/figma/icon-close.svg" alt="" width="24" height="24">
          </button>
        </div>
        <div class="tops__list" role="radiogroup" aria-labelledby="tops-title" data-tops-list></div>
      </div>
    </div>
  </div>`;
}

const topCardHTML = (t, color, on) => `
  <button class="tops-card" type="button" role="radio" aria-checked="${on}" tabindex="${on ? 0 : -1}" data-top="${t.id}">
    <span class="tops-card__media"><img class="tops-card__img" src="${t.image(color.slug)}" alt="" width="112" height="112" decoding="async" data-top-img></span>
    <span class="tops-card__text">
      <span class="tops-card__name">${tm(t.name)}</span>
      <span class="tops-card__color" data-top-color>${color.name}</span>
    </span>
    <span class="tops-card__icon" aria-hidden="true">
      <svg class="tops-card__plus" viewBox="0 0 24 24" width="24" height="24"><path d="M12 4v16M4 12h16" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
      <svg class="tops-card__tick" viewBox="0 0 24 24" width="24" height="24"><path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
    </span>
  </button>`;

export function initCrossSell({ getColor, onAdd, onSizeChart, items = CORE_TOPS }) {
  const CORE_TOPS = items; // the four tops, or the bundle page's one item
  const single = items.length < 2;
  const shown = (t, c = getColor()) => t.shown?.(c) || c;
  const root = $('[data-xsell]');
  root.toggleAttribute('data-single', single);
  const body = $('[data-xsell-body]', root);
  const media = $('[data-xsell-media]', root);
  const strip = $('[data-xsell-strip]', root);
  const nameBtn = $('[data-xsell-name]', root);
  const sizeBtn = $('[data-xsell-size]', root);
  const colorEl = $('[data-xsell-color]', root);
  const addBtn = $('[data-xsell-add]', root);
  const addLabel = $('[data-xsell-add-label]', root);
  const thumb = $('[data-xsell-thumb]', root);
  const live = $('[data-xsell-live]', root);

  const sel = { index: 0, size: null, type: null, chosen: false, adding: false }; // chosen: a top size was picked, so it stops following the pant's
  const cur = () => CORE_TOPS[sel.index];

  /* ---- the card ------------------------------------------------------ */
  strip.innerHTML = CORE_TOPS.map((t, i) => `
    <span class="xsell__slide" aria-hidden="${i !== 0}"><img class="xsell__img" src="${t.image(shown(t).slug)}" alt="" width="126" height="163" decoding="async" draggable="false"></span>`).join('');
  colorEl.textContent = shown(CORE_TOPS[0]).name;
  const imgs = $$('.xsell__img', strip);

  const sizeText = () => (sel.size ? `${sel.size}${sel.type && sel.type !== 'Regular' ? ` ${sel.type}` : ''}` : 'Size');
  function syncLabels({ animate: anim = false } = {}) {
    const t = cur();
    const name = t.shortName;
    if (anim) swapText(nameBtn, name); else nameBtn.textContent = name;
    if (anim) swapText(sizeBtn, sizeText()); else sizeBtn.textContent = sizeText();
    nameBtn.setAttribute('aria-label', single ? `${t.plainName}. View the product` : `${t.plainName}. Choose a different scrub top`);
    sizeBtn.setAttribute('aria-label', sel.size ? `Size ${SIZE_NAMES[sel.size]}${sel.type && sel.type !== 'Regular' ? ` ${sel.type}` : ''}. Change size` : `Select a size for the ${t.shortName}`);
    addBtn.setAttribute('aria-label', `Add the ${t.plainName} in ${shown(t).name}${sel.size ? `, ${SIZE_NAMES[sel.size]}` : ''} to your bag`);
  }

  /* ---- swipe + progress bar ------------------------------------------ */
  const width = () => media.clientWidth;
  const SPRING = { type: 'spring', stiffness: 320, damping: 34, restDelta: 0.5 };
  let stripAnim = null;
  let thumbAnim = null;
  const place = (x, { instant = false } = {}) => {
    stripAnim?.stop();
    if (instant || state.reduced) { stripAnim = null; animate(strip, { x }, { duration: 0 }); return; }
    stripAnim = track(animate(strip, { x }, SPRING));
  };
  /* the thumb is a quarter of the bar and moves a quarter per top: at 1.5
     it sits halfway between the second and third, as mid-drag */
  const placeThumb = (pos, { instant = false } = {}) => {
    const x = `${Math.max(0, Math.min(CORE_TOPS.length - 1, pos)) * 100}%`;
    thumbAnim?.stop();
    if (instant || state.reduced) { thumbAnim = null; animate(thumb, { x }, { duration: 0 }); return; }
    thumbAnim = track(animate(thumb, { x }, { ...SPRING, restDelta: 0.1 }));
  };
  thumb.style.width = `${100 / CORE_TOPS.length}%`;
  placeThumb(0, { instant: true });

  function go(i, { from = 'swipe' } = {}) {
    const next = Math.max(0, Math.min(CORE_TOPS.length - 1, i));
    const changed = next !== sel.index;
    sel.index = next;
    place(-next * width());
    placeThumb(next);
    $$('.xsell__slide', strip).forEach((s, k) => s.setAttribute('aria-hidden', String(k !== next)));
    if (!changed) return;
    /* the size carries over when the next top comes in it; its type starts fresh */
    sizeDrawer.setProduct(cur());
    ({ size: sel.size, type: sel.type } = sizeDrawer.value);
    syncLabels({ animate: true });
    live.textContent = `${cur().plainName} in ${getColor().name}, ${next + 1} of ${CORE_TOPS.length}`;
    if (from === 'drawer') play(media, { scale: [0.96, 1] }, SPRING_RELEASE);
  }

  /* touch (and mouse) drag on the card: horizontal intent only, so the page
     still scrolls vertically through it; past either end it rubber-bands */
  let drag = null;
  let suppress = false;
  body.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || single) return;
    drag = { id: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now(), on: false, dx: 0 };
  });
  body.addEventListener('pointermove', (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    const dx = e.clientX - drag.x;
    const dy = e.clientY - drag.y;
    if (!drag.on) {
      if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) {
        if (Math.abs(dy) > 10) drag = null; // a scroll, not a swipe
        return;
      }
      drag.on = true;
      body.setPointerCapture(e.pointerId);
      body.classList.add('is-dragging');
      stripAnim?.stop();
    }
    const edge = (sel.index === 0 && dx > 0) || (sel.index === CORE_TOPS.length - 1 && dx < 0);
    drag.dx = edge ? dx * 0.3 : dx;
    animate(strip, { x: -sel.index * width() + drag.dx }, { duration: 0 });
    placeThumb(sel.index - drag.dx / width(), { instant: true });
  });
  const end = (e) => {
    if (!drag || (e && e.pointerId !== drag.id)) return;
    const d = drag;
    drag = null;
    body.classList.remove('is-dragging');
    if (!d.on) return;
    suppress = true;
    setTimeout(() => { suppress = false; }, 0);
    const v = d.dx / Math.max(1, performance.now() - d.t); // px per ms
    const step = Math.abs(d.dx) > width() * 0.22 || Math.abs(v) > 0.45 ? -Math.sign(d.dx) : 0;
    if (step && sel.index + step >= 0 && sel.index + step < CORE_TOPS.length) go(sel.index + step);
    else { place(-sel.index * width()); placeThumb(sel.index); }
  };
  body.addEventListener('pointerup', end);
  body.addEventListener('pointercancel', end);
  body.addEventListener('click', (e) => { if (suppress) { e.preventDefault(); e.stopPropagation(); } }, true);
  addEventListener('resize', () => place(-sel.index * width(), { instant: true }));

  /* ---- the tops drawer ----------------------------------------------- */
  document.body.insertAdjacentHTML('beforeend', topsDrawerHTML());
  const topsRoot = $('[data-drawer="tops"]');
  const tops = createDrawer(topsRoot);
  const list = $('[data-tops-list]', topsRoot);
  let closeTimer = 0;
  const renderTops = () => {
    list.innerHTML = CORE_TOPS.map((t, i) => topCardHTML(t, getColor(), i === sel.index)).join('');
  };
  list.addEventListener('click', (e) => {
    const card = e.target.closest('[data-top]');
    if (!card) return;
    const i = CORE_TOPS.findIndex((t) => t.id === card.dataset.top);
    $$('[data-top]', list).forEach((c, k) => { c.setAttribute('aria-checked', String(k === i)); c.tabIndex = k === i ? 0 : -1; });
    play(card, { scale: [MO.scalePress, 1] }, SPRING_RELEASE);
    announce(`${CORE_TOPS[i].plainName} selected`);
    clearTimeout(closeTimer);
    /* the card changes as the drawer leaves, so the change is seen */
    closeTimer = setTimeout(() => { tops.close(); go(i, { from: 'drawer' }); }, state.reduced ? 200 : AUTO_CLOSE_MS);
  });
  list.addEventListener('keydown', (e) => {
    const cards = $$('[data-top]', list);
    const k = cards.indexOf(document.activeElement);
    const d = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
    if (k < 0 || !d) return;
    e.preventDefault();
    const n = (k + d + cards.length) % cards.length;
    cards.forEach((c, j) => { c.tabIndex = j === n ? 0 : -1; });
    cards[n].focus();
  });
  topsRoot.addEventListener('drawer:close', () => clearTimeout(closeTimer));

  nameBtn.addEventListener('click', () => {
    if (single) { open(cur().url, '_blank', 'noopener'); return; }
    renderTops();
    $('[aria-checked="true"]', list)?.setAttribute('data-autofocus', '');
    tops.open(nameBtn);
    if (!state.reduced) {
      play($$('[data-top]', list), { opacity: [0, 1], y: [MO.riseXs, 0] },
        { duration: MO.base, ease: MO.eEntrance, delay: stagger(MO.stagTight, { startDelay: 0.16 }) });
    }
  });

  /* ---- the size drawer: the PDP's own, cloned ------------------------ */
  /* no model line or CHANGE MODEL here: the model on the page wears the
     pant, so the fit tip isn't about the top being sized */
  const sizeRoot = $('[data-drawer="size"]').cloneNode(true);
  sizeRoot.dataset.drawer = 'xsell-size';
  $('.size-drawer__fit-row', sizeRoot)?.remove();
  $$('[id]', sizeRoot).forEach((el) => { el.id = `xs-${el.id}`; });
  $$('[aria-labelledby]', sizeRoot).forEach((el) => el.setAttribute('aria-labelledby', `xs-${el.getAttribute('aria-labelledby')}`));
  $('[data-size-grid]', sizeRoot).innerHTML = '';
  document.body.append(sizeRoot);
  let addAfterPick = false;
  const sizeDrawer = initSizeDrawer({
    root: sizeRoot,
    product: cur(),
    preselectType: true,
    onPick(size, type) {
      sel.size = size;
      sel.type = type;
      sel.chosen = true;
      setTimeout(() => {
        syncLabels({ animate: true });
        if (addAfterPick) { addAfterPick = false; add(); }
      }, state.reduced ? 0 : 140);
    },
    onSizeChart: (link, back, marked) => onSizeChart(link, back, { ...marked, chart: cur().chart || 'tops' }),
  });
  sizeBtn.addEventListener('click', () => { addAfterPick = false; sizeDrawer.open(sizeBtn); });

  /* ---- add to bag ------------------------------------------------------ */
  async function add() {
    if (sel.adding) return;
    if (!sel.size) {
      /* no size yet: the size drawer first, then the add — unless it's closed without one */
      addAfterPick = true;
      sizeDrawer.open(addBtn, { onDismiss: () => { addAfterPick = false; } });
      return;
    }
    sel.adding = true;
    const img = imgs[sel.index];
    const item = {
      id: cur().id, name: cur().plainName, fullName: cur().name, color: shown(cur()).name,
      size: sel.size, type: sel.type, price: cur().price, image: img?.currentSrc || img?.src,
      model: cur().model?.(shown(cur()).slug), // on model, for the bag
    };
    onAdd(item, img);
    addBtn.dataset.state = 'added';
    play(media, { scale: [1, 1.04, 1] }, { duration: MO.slow, ease: MO.eStandard });
    await play(addLabel, { y: ['0%', '-120%'], opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit })?.finished;
    addLabel.innerHTML = `${CHECK}<span>Added</span>`;
    play(addLabel, { y: ['120%', '0%'], opacity: [0, 1] }, { duration: MO.slow, ease: MO.eSpring });
    const tick = $('.xsell__check path', addLabel);
    if (tick && !state.reduced) animate(tick, { pathLength: [0, 1] }, { duration: MO.slow, ease: MO.eEntrance, delay: 0.12 });
    await new Promise((r) => setTimeout(r, state.reduced ? 1200 : 2000));
    await play(addLabel, { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit })?.finished;
    addLabel.textContent = 'Add to Bag';
    delete addBtn.dataset.state;
    play(addLabel, { opacity: [0, 1], y: [4, 0] }, { duration: MO.base, ease: MO.eEntrance });
    sel.adding = false;
  }
  addBtn.addEventListener('click', add);

  syncLabels();

  return {
    /* the pant's swatch changed: every top follows it */
    setColor(c) {
      imgs.forEach((img, i) => { img.src = CORE_TOPS[i].image(shown(CORE_TOPS[i], c).slug); });
      swapText(colorEl, shown(cur(), c).name);
      if (!state.reduced) play(media, { opacity: [0.4, 1] }, { duration: MO.base, ease: MO.eEntrance });
      syncLabels();
      if (tops.isOpen) {
        $$('[data-top]', list).forEach((card, i) => {
          $('[data-top-img]', card).src = CORE_TOPS[i].image(c.slug);
          $('[data-top-color]', card).textContent = c.name;
        });
      }
    },
    /* the pant's size: the card follows it until a top size is picked */
    setPantSize(size) {
      if (sel.chosen || !size || !cur().sizes.includes(size)) return;
      sel.size = size;
      sizeDrawer.preset(size);
      sel.type = sizeDrawer.value.type;
      syncLabels({ animate: true });
    },
    close() { tops.close(); sizeDrawer.close(); },
  };
}
